'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

interface ReviewApproveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasBatch: boolean;
  originalTotalSheets: number;
  isApproving: boolean;
  onApprove: () => void;
}

export function ReviewApproveDialog({
  open,
  onOpenChange,
  hasBatch,
  originalTotalSheets,
  isApproving,
  onApprove
}: ReviewApproveDialogProps) {
  const t = useTranslations('review');
  const [approvePageCount, setApprovePageCount] = useState('');

  return (
    <AlertDialog
      open={open}
      onOpenChange={(val) => {
        if (!val) setApprovePageCount('');
        onOpenChange(val);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('approveTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('approveDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {hasBatch && (
          <div className='space-y-1.5'>
            <label htmlFor='approve-page-count' className='text-sm font-medium'>
              {t('reviewerPageCountLabel')}
            </label>
            <Input
              id='approve-page-count'
              type='number'
              min={1}
              placeholder={t('reviewerPageCountPlaceholder')}
              value={approvePageCount}
              onChange={(e) => setApprovePageCount(e.target.value)}
              className={cn(
                approvePageCount !== '' &&
                  Number(approvePageCount) !== originalTotalSheets &&
                  'border-red-300 focus-visible:ring-red-500'
              )}
            />
            {approvePageCount !== '' &&
              Number(approvePageCount) !== originalTotalSheets && (
                <p className='text-xs text-red-600 dark:text-red-400'>
                  {t('pageCountMismatch')}
                </p>
              )}
            {approvePageCount !== '' &&
              Number(approvePageCount) === originalTotalSheets && (
                <p className='text-xs text-green-600 dark:text-green-400'>
                  <Check className='me-1 inline h-3 w-3' />
                  {t('pageCountMatch')}
                </p>
              )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onApprove}
            className='bg-green-600 text-white hover:bg-green-700'
            disabled={
              isApproving ||
              (hasBatch && Number(approvePageCount) !== originalTotalSheets)
            }
          >
            {t('approve')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
